import { useState, useEffect } from 'react'
import {
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  TextInput,
  StyleSheet,
  SafeAreaView,
  Keyboard
} from 'react-native'
import { Ionicons, FontAwesome } from '@expo/vector-icons'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'

// styles
import { colorPalette, globalStyles } from '../../static/styles/global'

// components
import Card from '../../components/shared/card/Card'
import CardSeparator from '../../components/shared/card/CardSeparator'
import { Message } from '../../types/communicateTypes'
import ScreenStatus from '../../components/home/ScreenStatus'
import MyScrollView from '../../components/shared/navigator/MyScrollView'

// stores
import { useCommunicateStore } from '../../stores/communicate'
import HomeDetailsHeader from '../../components/home/HomeDetailsHeader'

// skeleton
import CommunicateSkeleton from '../../skeletons/CommunicateSkeleton'
import SendReplyMessages from '../../components/communication/SendReplyMessages'
import { messageResponseTitle } from '../../helpers/communicateHelper'
//translation
import { useTranslation } from 'react-i18next'

const MessageScreen = ({ navigation, route }): JSX.Element => {
  const [messageText, setMessageText] = useState('')
  const [savedMessageText, setSavedMessageText] = useState('')

  // params
  const { params } = route
  const { mostRecentUserOnBoardings: userId } = params

  const message: any = params['message']
  const isEmployeeMessage = message?.isEmployeeMessage

  // stores' data
  const {
    messages: messages,
    loading: loading,
    error: error,
    clearData,
    fetchAllMessages,
    replyToMessage,
    patchEmployeeMessage
  } = useCommunicateStore()

  const handleSendMessage = () => {
    Keyboard.dismiss()
    setSavedMessageText(messageText.trim())

    if (isEmployeeMessage) {
      patchEmployeeMessage(
        message.receiverEssUserOnBoardingKey,
        message.essEmployeeMessageKey,
        {
          response: messageText.trim(),
          isNew: false,
          isDeleted: false
        }
      )
    } else {
      replyToMessage(message.essUserOnBoardingKey, message.essMessageKey, {
        message: messageText.trim()
      })
    }
    setMessageText('')
    // navigation.replace('CommunicateWithAdmin')
    navigation.replace('Onboarding')
  }

  if (loading) return <CommunicateSkeleton />

  if (error) return <ScreenStatus message={error} />
  const { t } = useTranslation()

  const headerTitle = isEmployeeMessage ? message.subject : t('messagehiringmanager')

  const sentMessageLabel = isEmployeeMessage
    ? (message.senderEssUserOnBoardingKey === userId ? t('you') : (message.senderName || ''))
    : messageResponseTitle(message.initiatorType).message

  const replyMessageLabel = isEmployeeMessage
    ? (message.senderEssUserOnBoardingKey === userId ? (message.receiverName || '') : t('you'))
    : messageResponseTitle(message.initiatorType).response

  return (
    <KeyboardAwareScrollView keyboardShouldPersistTaps={'always'}>
      <SafeAreaView>
        <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
          <View style={[globalStyles.horizontalPadGap]}>
            <HomeDetailsHeader title={headerTitle} />
          </View>
        </View>
        <View style={[globalStyles.containerOverlap, globalStyles.mb4]}>
          <Card>
            <SafeAreaView style={[{ flexGrow: 1 }]}>
              <View
                style={[
                  globalStyles.hDirection,
                  globalStyles.vCenterItems,
                  globalStyles.hBetweenItems
                ]}
              >
                <Text
                  style={[
                    globalStyles.text,
                    globalStyles.fwBold,
                    globalStyles.fs18,
                    globalStyles.blackTextColor
                  ]}
                >
                  {message.subject}
                </Text>
              </View>
              <CardSeparator light={true} />
              <View style={[styles.msgWrapper]}>
                <MyScrollView style={[styles.body, styles.textArea]}>
                  <SendReplyMessages
                    message={message.message}
                    reply={message.response}
                    sentMessageLabel={sentMessageLabel}
                    sentMessageDate={message.messageSentDate}
                    replyMessageLabel={replyMessageLabel}
                    replyMessageDate={message.messageResponseDate}
                    initiatorType={message.initiatorType}
                  />

                  {/* TODO - the print out's will be removed */}
                  {/* <>
                    <Text>{JSON.stringify(messageText, null, 2)}</Text>
                    <Text>{JSON.stringify(savedMessageText, null, 2)}</Text>
                    <Text>{JSON.stringify(message, null, 2)}</Text>
                  </> */}
                </MyScrollView>

                <View style={[styles.footer, styles.noBorder]}>
                  {message.isResponseAllowed && (
                    <View
                      style={[globalStyles.flexOne, styles.messageInputWrapper]}
                    >
                      <TextInput
                        multiline
                        numberOfLines={1}
                        style={[globalStyles.input, styles.messageInput]}
                        placeholder={t('message')}
                        placeholderTextColor={colorPalette.lightGray}
                        value={messageText}
                        onChangeText={(text) => setMessageText(text)}
                      />
                      <TouchableOpacity
                        style={[styles.messageSend]}
                        onPress={handleSendMessage}
                        disabled={messageText.trim() === ''}
                      >
                        <FontAwesome
                          name="send"
                          size={26}
                          color={colorPalette.secondary}
                          style={[
                            messageText.trim() === '' && { opacity: 0.3 }
                          ]}
                        />
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              </View>
            </SafeAreaView>
          </Card>
        </View>
      </SafeAreaView>
    </KeyboardAwareScrollView>
  )
}

const styles = StyleSheet.create({
  labelSubject: {
    borderLeftWidth: 2,
    borderLeftColor: colorPalette.secondary,
    paddingLeft: 10
  },
  inputSubject: {
    borderWidth: 0,
    borderRadius: 0,
    borderBottomColor: colorPalette.darkLight,
    borderBottomWidth: 1,
    paddingHorizontal: 5
  },
  // body: {
  //   paddingTop: 20,
  //   paddingBottom: 30,
  //   paddingHorizontal: 20
  // },
  body: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingHorizontal: 20
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '100%',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderTopWidth: 1,
    borderColor: colorPalette.darkLight
  },
  noBorder: {
    borderColor: 'transparent'
  },
  textArea: {
    minHeight: 100,
    maxHeight: 300,
    paddingVertical: 20,
    paddingHorizontal: 10
  },
  msgWrapper: {
    marginHorizontal: -10,
    marginVertical: -10
  },
  msgRequest: {
    marginLeft: 70
  },
  msgResponse: {
    marginRight: 70
  },
  msgBox: {
    backgroundColor: colorPalette.lightBlue,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginBottom: 6
  },
  msgBoxResponse: {
    backgroundColor: colorPalette.light
  },
  messageInput: {
    textAlignVertical: 'top',
    borderColor: colorPalette.lightGray,
    borderRadius: 50,
    minHeight: 44,
    paddingHorizontal: 20,
    paddingRight: 60,
    paddingTop: 11
  },
  messageInputWrapper: {
    position: 'relative'
  },
  messageSend: {
    position: 'absolute',
    right: 18,
    top: '50%',
    marginTop: -11
  }
})

export default MessageScreen
